import { useCallback, useEffect, useMemo } from "react";
import {
  useAggregatedActivities,
  type ActivityResponse,
  type AggregatedActivityResponse,
} from "@stream-io/feeds-react-sdk";
import { skipToken, useQuery } from "@tanstack/react-query";
import {
  fetchStoriesToken,
  type StoriesTokenData,
} from "Api/Queries/Stories/useStoriesTokenQuery";
import type {
  Story,
  StoryAuthorKind,
  StoryGroup,
} from "Components/Stories/StoriesSection";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { useDisclosedNameResolver } from "Hooks/Chat/useDisclosedNameResolver";
import {
  connectStoriesFeed,
  StoriesConnectSupersededError,
  synchronizeStoriesFeedForToken,
} from "Utils/Stories/storiesFeedsClient";
import { isConnectSupersededError } from "Utils/ConnectSupersededError";
import {
  getStoryActivityCustomData,
  getStreamUserAccessRole,
} from "Utils/Stories/storyActivityCustom";

const STORIES_FEED_QUERY_KEY = "STORIES_FEED";

export const storiesFeedKeys = {
  all: [STORIES_FEED_QUERY_KEY],
  byPersona: (apiKey?: string, userId?: string) => [
    ...storiesFeedKeys.all,
    apiKey,
    userId,
  ],
};

/**
 * Actor id of the single brand circle. Stream aggregates the timeline per author, so two Admin
 * personas arrive as two groups — but they present as ONE source (Mo.one), so they are merged
 * client-side under this synthetic id. Constant even for a lone Admin group, so an open viewer
 * keeps its selection when a second Admin posts mid-session.
 */
export const MOONE_STORY_GROUP_ACTOR_ID = "moone";

/** Stories from Mo.one admins present as the brand; merchants as themselves. */
const getAuthorKind = (accessRoleCode?: string): StoryAuthorKind =>
  accessRoleCode === AccessRoleCodes.Admin ||
  accessRoleCode === AccessRoleCodes.AdminLite
    ? "moone"
    : "merchant";

const toStory = (activity: ActivityResponse): Story => ({
  id: activity.id,
  imageUrl: activity.attachments.find(
    (attachment) => attachment.type === "image",
  )?.image_url,
  videoUrl: activity.attachments.find(
    (attachment) => attachment.type === "video",
  )?.asset_url,
  text: activity.text,
  ...getStoryActivityCustomData(activity.custom),
  time: activity.created_at.toISOString(),
  isRead: activity.is_watched ?? false,
});

/**
 * Instagram-style within-source order: oldest → newest, so new stories append on the right
 * (Stream returns activities newest-first). ISO timestamps compare lexicographically.
 */
const sortOldestFirst = (stories: Array<Story>): Array<Story> =>
  [...stories].sort((a, b) => (a.time ?? "").localeCompare(b.time ?? ""));

/**
 * Collapses every brand (Admin) group into one Mo.one circle at the position of the first one;
 * merchant groups pass through untouched. Watched state keeps working unchanged because it is
 * marked per activity id, not per group; the merged ring greys only once EVERY brand story is
 * read, like any other source. The brand circle renders the logo by authorKind, so no actor
 * image is needed.
 */
const mergeMooneGroups = (
  groups: Array<StoryGroup>,
  mooneName: string,
): Array<StoryGroup> => {
  const mooneGroups = groups.filter((group) => group.authorKind === "moone");
  const [firstMooneGroup] = mooneGroups;
  if (!firstMooneGroup) {
    return groups;
  }

  const stories = sortOldestFirst(
    mooneGroups.flatMap((group) => group.stories),
  );
  const merged: StoryGroup = {
    actorId: MOONE_STORY_GROUP_ACTOR_ID,
    actorName: mooneName,
    authorKind: "moone",
    stories,
    isRead: stories.every((story) => story.isRead),
  };

  return groups.flatMap((group) => {
    if (group === firstMooneGroup) {
      return [merged];
    }
    return group.authorKind === "moone" ? [] : [group];
  });
};

/**
 * The stories timeline aggregates by actor, so one aggregated group = one source (avatar circle).
 * Actor display data comes from Stream user-profile enrichment — the backend re-upserts author
 * profiles (name, avatar, accessRole) on every token issuance, so no extra API call is needed.
 * The enriched name is the DISCLOSABLE one (the @handle for a private author), so `resolveName`
 * upgrades it to the real name for the viewer's own mutual followers — same rule as chat (ADR-0007).
 */
const toStoryGroup = (
  group: AggregatedActivityResponse,
  mooneName: string,
  resolveName: (
    userId: string | undefined,
    streamName: string | undefined,
  ) => string | undefined,
  anonymousName: string,
): StoryGroup | null => {
  const user = group.activities[0]?.user;
  if (!user) {
    return null;
  }

  const authorKind = getAuthorKind(getStreamUserAccessRole(user.custom));
  const stories = sortOldestFirst(group.activities.map(toStory));

  return {
    actorId: user.id,
    actorName:
      authorKind === "moone"
        ? mooneName
        : // Never fall back to user.id — that is the raw persona uuid, and a private author with
          // no handle yet legitimately reaches Stream without a name at all.
          (resolveName(user.id, user.name) ?? anonymousName),
    actorImage: user.image,
    authorKind,
    stories,
    // Stream flips the group flag only once EVERY story of the source is watched.
    isRead: group.is_watched ?? stories.every((story) => story.isRead),
  };
};

/**
 * Live view of the caller's stories timeline: connects the Feeds client for the current persona,
 * opens the `stories` feed with watch: true and projects the SDK's reactive aggregated state into
 * the presentation model. Watched state is written back per ACTIVITY id via
 * `markStoriesWatched` — Stream updates the reactive state through the WebSocket afterwards.
 *
 * The connection lives in a React Query query so transient connect failures retry with backoff
 * (and an errored query re-fetches on the next window focus/remount), and a persona switch
 * synchronously resets `data` to undefined — no frame renders the previous persona's groups.
 * A superseded connect is a cancellation that `reportApiError` drops; a real connect failure is
 * reported to Sentry.
 */
export const useStoriesFeed = (
  storiesToken: StoriesTokenData | undefined,
  mooneName: string,
  anonymousName: string,
) => {
  const resolveName = useDisclosedNameResolver();

  const {
    data: feed,
    isPending,
    isFetching,
  } = useQuery({
    // Keyed by persona, NOT by token string — a token refetch must not reconnect the feed;
    // token freshness is the token provider's job.
    queryKey: storiesFeedKeys.byPersona(
      storiesToken?.apiKey,
      storiesToken?.userId,
    ),
    queryFn: storiesToken
      ? ({ signal }) =>
          connectStoriesFeed(
            storiesToken,
            async () => {
              // Fresh token each call — the SDK invokes this on connect AND on expiry refresh.
              // Never hand a foreign persona's token to this connection: if the active
              // IdentityUser changed under us, the key change drives the reconnect instead —
              // this connect is superseded, not failed. (feeds-client retries a failing provider
              // twice more, 1s apart, before surfacing the throw — expect ~3s and three token
              // fetches on this path.)
              const fresh = await fetchStoriesToken();
              if (fresh.userId !== storiesToken.userId) {
                throw new StoriesConnectSupersededError(
                  "Stories token persona changed",
                );
              }
              return fresh.token;
            },
            signal,
          )
      : skipToken,
    // A resolved Feed never goes stale — the WebSocket keeps it live; the module singleton in
    // storiesFeedsClient is the real cache, so the query entry itself can be dropped when unused.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    // The Feed is a live class instance — React Query must not walk/replace its object graph.
    structuralSharing: false,
    // Cause-aware: the SDK rewraps a token-provider throw, see isConnectSupersededError.
    retry: (failureCount, error) =>
      !isConnectSupersededError(error) && failureCount < 3,
  });

  // A new token for the same persona means the backend re-ran the follow ensure (e.g. after a
  // follow, invalidateSocialGraph refetches the token): re-read the live timeline so the newly
  // followed author's existing stories appear without a persona switch or re-login. The helper
  // deduplicates on the token string, so a cached token on remount is a no-op.
  useEffect(() => {
    if (storiesToken) {
      void synchronizeStoriesFeedForToken(storiesToken);
    }
  }, [storiesToken]);

  const aggregated = useAggregatedActivities(feed);

  const storyGroups = useMemo(() => {
    if (!aggregated?.aggregated_activities) {
      return [];
    }
    return mergeMooneGroups(
      aggregated.aggregated_activities
        .map((group) =>
          toStoryGroup(group, mooneName, resolveName, anonymousName),
        )
        .filter((group): group is StoryGroup => group !== null),
      mooneName,
    );
  }, [
    aggregated?.aggregated_activities,
    mooneName,
    resolveName,
    anonymousName,
  ]);

  // Fire-and-forget: watched state is best-effort — a rejection must never break a caller's
  // exit flow, and Stream re-syncs the flags on the next session anyway.
  const markStoriesWatched = useCallback(
    (activityIds: Array<string>) => {
      if (!feed || activityIds.length === 0) {
        return;
      }
      feed.markActivity({ mark_watched: activityIds }).catch(() => undefined);
    },
    [feed],
  );

  return {
    storyGroups,
    // isPending alone would be true forever under skipToken (non-migrated users) — the section
    // must stay hidden for them, so only a genuinely in-flight connect counts as loading.
    isLoading: (isPending && isFetching) || (aggregated?.is_loading ?? false),
    markStoriesWatched,
  };
};
